import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import EmailEditor, { EditorRef, EmailEditorProps } from "react-email-editor";
import { Option } from "@spike/model";
import { RootState } from "store";
import {
  fetchEditorConfigThunk,
  fetchTemplateThunk,
  reset as resetSystemEmailTemplates,
  saveTemplateThunk,
  updateTemplateThunk
} from "actions/systemEmailTemplates/SystemEmailTemplatesActions";
import {
  DEFAULT_CATEGORY,
  EMAIL_TEMPLATE_CATEGORIES,
  EmailEditorConfig,
  EmailTemplateCategory,
  EmailTemplateSavePayload,
  MergeTag,
  SystemEmailTemplate
} from "model/SystemEmailTemplate";
import { FieldError } from "model";
import { Button, ConfirmDialog, SelectField, Spinner, TextField } from "components/UI";

/**
 * Derived from the SDK's signature so we don't need to know the exact export
 * name of the design type (`JSONTemplate<"email">` in current versions of
 * `react-email-editor`). Stays correct even if Unlayer renames the type later.
 */
type UnlayerDesign = Parameters<NonNullable<EditorRef["editor"]>["loadDesign"]>[0];

interface Props {
  /** undefined → create new; number → edit existing. */
  templateId?: number;
  /** Called after a successful save AND on cancel/discard. The parent flips
   *  back to list mode and the reducer has already spliced the saved row in. */
  onExit: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%",
      backgroundColor: "#fff"
    },
    topBar: {
      display: "flex",
      alignItems: "flex-end",
      gap: "16px",
      padding: "16px 24px",
      borderBottom: "1px solid #E5E5E5",
      flexShrink: 0,
      backgroundColor: "#fff"
    },
    nameField: {
      flex: "2 1 320px",
      minWidth: "240px"
    },
    categoryField: {
      flex: "1 1 220px",
      minWidth: "180px"
    },
    actions: {
      display: "flex",
      gap: "12px",
      flexShrink: 0,
      marginLeft: "auto",
      // Push the buttons down a hair so they line up with the field inputs
      // (their labels add vertical space above the input baseline).
      paddingBottom: "2px"
    },
    editorContainer: {
      flex: "1 1 auto",
      display: "flex",
      minHeight: 0
    },
    fullCenter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "100%"
    },
    errorBox: {
      padding: "32px",
      textAlign: "center",
      maxWidth: "480px",
      margin: "auto",
      fontFamily: "Poppins"
    },
    errorTitle: {
      fontWeight: 600,
      fontSize: "20px",
      marginBottom: "12px"
    },
    errorBody: {
      color: "#555",
      marginBottom: "20px"
    }
  })
);

/**
 * Turn merge tags from the model's array shape into Unlayer's expected
 * keyed-object shape: `{ first_name: { name, value }, ... }`. Direct port of
 * the marketplace helper — we copy the source as-is because the integration
 * contract with the SDK is identical.
 */
const buildUnlayerMergeTags = (mergeTags: Array<MergeTag>) => {
  return mergeTags.reduce<Record<string, { name: string; value: string }>>((acc, tag) => {
    const key = tag.value.replace(/[{}]/g, "").trim();
    if (key) {
      acc[key] = { name: tag.name, value: tag.value };
    }
    return acc;
  }, {});
};

const NAME_FIELD = "name";
const CATEGORY_FIELD = "category";

/**
 * Standalone editor for system email templates. Adapted from the marketplace's
 * `EmailTemplateEditorComponent` but:
 *   - No embedded mode (admin doesn't have a wizard overlay)
 *   - No auto-generated name (CUD-4860 requires super admins to enter one)
 *   - No subject_default field (product decision)
 *   - Category picker is visibly exposed (vs hidden in marketplace)
 *   - MUI v4 chrome instead of V3 FullWindowModal
 *
 * Lifecycle:
 *   - Mount: fetch unlayerConfig (once per session — cached in slice) +
 *     (edit only) fetch the template for its html/designJson.
 *   - Unmount: dispatch reset() to clear `currentTemplate` so the next open
 *     starts fresh.
 *   - On `onReady` from EmailEditor: flip `editorReady`. In edit mode this
 *     triggers `loadDesign` against the slice's `currentTemplate.designJson`.
 *
 * Save flow:
 *   - Validate name (required) + category (always present, defaults to
 *     `engagement`). Category is in the union by construction; no extra check.
 *   - `editor.exportHtml(data => ...)` — Unlayer hands back `{ html, design }`.
 *   - Dispatch save/update; both thunks resolve to the saved template (or
 *     `null` on failure). On success call `onExit` so the parent returns to
 *     the list (which already has the saved row from the reducer).
 *
 * Cancel flow:
 *   - If `dirty`, open ConfirmDialog. Otherwise exit immediately.
 *   - `dirty` is approximated (Unlayer doesn't expose a dirty signal) by
 *     assuming any session after onReady or any name/category edit counts.
 */
export const EmailTemplateEditor: FunctionComponent<Props> = ({ templateId, onExit }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const isEditMode = typeof templateId === "number";

  const unlayerConfig = useSelector<RootState, EmailEditorConfig | undefined>(
    s => s.systemEmailTemplates.unlayerConfig
  );
  const loadingConfig = useSelector<RootState, boolean>(s => s.systemEmailTemplates.loadingConfig);
  const currentTemplate = useSelector<RootState, SystemEmailTemplate | undefined>(
    s => s.systemEmailTemplates.currentTemplate
  );
  const loadingTemplate = useSelector<RootState, boolean>(s => s.systemEmailTemplates.loadingTemplate);
  const saving = useSelector<RootState, boolean>(s => s.systemEmailTemplates.saving);

  const editorRef = useRef<EditorRef | null>(null);

  const [name, setName] = useState<string>("");
  const [category, setCategory] = useState<EmailTemplateCategory>(DEFAULT_CATEGORY);
  const [errors, setErrors] = useState<Array<FieldError>>([]);
  const [editorReady, setEditorReady] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [prefilled, setPrefilled] = useState<boolean>(false);
  const [confirmExitOpen, setConfirmExitOpen] = useState<boolean>(false);
  /**
   * Which button is currently mid-save. Purely visual — the global `saving`
   * flag still gates both buttons, but this lets us show the spinner on the
   * one the user actually clicked.
   */
  const [savingAs, setSavingAs] = useState<"draft" | "publish" | null>(null);

  // ---------------------------------------------------------------------
  // Initial loading: editor config + template (if edit)
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!unlayerConfig) {
      dispatch(fetchEditorConfigThunk());
    }
    if (isEditMode && templateId !== undefined) {
      dispatch(fetchTemplateThunk(templateId));
    }
    return () => {
      dispatch(resetSystemEmailTemplates());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill name + category from the loaded template (edit mode only).
  useEffect(() => {
    if (isEditMode && currentTemplate && !prefilled) {
      setName(currentTemplate.name);
      setCategory(currentTemplate.category);
      setPrefilled(true);
    }
  }, [isEditMode, currentTemplate, prefilled]);

  // Once both the editor is ready AND template is loaded, push the design.
  useEffect(() => {
    if (!editorReady) return;
    if (isEditMode && currentTemplate?.designJson && editorRef.current?.editor) {
      try {
        editorRef.current.editor.loadDesign(currentTemplate.designJson as unknown as UnlayerDesign);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[EmailTemplateEditor] Failed to load design", e);
      }
    }
  }, [editorReady, isEditMode, currentTemplate]);

  // Mark form as dirty after first ready or any field edit.
  useEffect(() => {
    if (editorReady) setDirty(true);
  }, [name, category, editorReady]);

  // ---------------------------------------------------------------------
  // Memoised options for the SDK
  // ---------------------------------------------------------------------
  const unlayerMergeTags = useMemo(
    () => (unlayerConfig ? buildUnlayerMergeTags(unlayerConfig.mergeTags) : {}),
    [unlayerConfig]
  );

  const editorOptions = useMemo<EmailEditorProps["options"]>(() => {
    if (!unlayerConfig) return {};
    return {
      user: unlayerConfig.userId
        ? {
            id: unlayerConfig.userId,
            ...(unlayerConfig.signature ? { signature: unlayerConfig.signature } : {})
          }
        : undefined,
      mergeTags: unlayerMergeTags
    };
  }, [unlayerConfig, unlayerMergeTags]);

  // ---------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------
  const handleEditorReady: EmailEditorProps["onReady"] = () => {
    setEditorReady(true);
  };

  const cleanError = (fieldName: string) => {
    setErrors(prev => prev.filter(e => e.fieldName !== fieldName));
  };

  const handleNameChange = (value: string) => {
    cleanError(NAME_FIELD);
    setName(value);
  };

  const handleCategoryChange = (option: Option<string | number>) => {
    cleanError(CATEGORY_FIELD);
    setCategory(option.id as EmailTemplateCategory);
  };

  const validate = (): boolean => {
    const next: Array<FieldError> = [];
    if (!name.trim()) {
      next.push({ fieldName: NAME_FIELD, errorMessage: "Name is required" });
    }
    setErrors(next);
    return next.length === 0;
  };

  const handleSaveClick = (publish: boolean) => {
    if (!validate()) return;

    if (!editorRef.current?.editor) {
      // eslint-disable-next-line no-console
      console.error("[EmailTemplateEditor] Editor not ready when Save clicked");
      return;
    }

    setSavingAs(publish ? "publish" : "draft");

    editorRef.current.editor.exportHtml(async data => {
      const payload: EmailTemplateSavePayload = {
        name: name.trim(),
        category,
        html: data.html,
        designJson: data.design as object,
        published: publish
      };

      // Thunks resolve to the saved template (or null). Cast through `any` —
      // redux-thunk's default Dispatch typing here returns the action union,
      // not the inner promise, so TS can't infer the resolved value.
      const result = isEditMode && templateId !== undefined
        ? await (dispatch(updateTemplateThunk(templateId, payload)) as any)
        : await (dispatch(saveTemplateThunk(payload)) as any);

      setSavingAs(null);

      if (result) {
        onExit();
      }
    });
  };

  const handleCancelClick = () => {
    if (dirty) {
      setConfirmExitOpen(true);
    } else {
      onExit();
    }
  };

  const handleConfirmExit = () => {
    setConfirmExitOpen(false);
    onExit();
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  // Editor not configured on the BE (project_id missing in credentials).
  if (unlayerConfig && !unlayerConfig.projectId) {
    return (
      <Box className={classes.container}>
        <Box className={classes.fullCenter}>
          <Box className={classes.errorBox}>
            <Box className={classes.errorTitle}>Email editor is not configured</Box>
            <Box className={classes.errorBody}>
              The Unlayer project id is not set on this environment. Please contact engineering.
            </Box>
            <Button label="Back to templates" onClick={onExit} />
          </Box>
        </Box>
      </Box>
    );
  }

  // Initial load.
  if (loadingConfig || !unlayerConfig || (isEditMode && (loadingTemplate || !currentTemplate))) {
    return (
      <Box className={classes.container}>
        <Box className={classes.fullCenter}>
          <Spinner />
        </Box>
      </Box>
    );
  }

  const selectedCategory: Option<string | number> | undefined = EMAIL_TEMPLATE_CATEGORIES.find(
    c => c.id === category
  ) as Option<string | number> | undefined;

  return (
    <Box className={classes.container}>
      <Box className={classes.topBar}>
        <Box className={classes.nameField}>
          <TextField
            name={NAME_FIELD}
            label="Name"
            value={name}
            onChange={handleNameChange}
            errors={errors}
            autoFocus={!isEditMode}
            disabled={saving}
          />
        </Box>

        <Box className={classes.categoryField}>
          <SelectField
            name={CATEGORY_FIELD}
            label="Category"
            selectedOption={selectedCategory}
            options={EMAIL_TEMPLATE_CATEGORIES as Array<Option<string | number>>}
            onSelect={handleCategoryChange}
            disabled={saving}
          />
        </Box>

        <Box className={classes.actions}>
          <Button
            label="Cancel"
            color="black"
            onClick={handleCancelClick}
            disabled={saving}
          />
          <Button
            label="Save as Draft"
            color="black"
            onClick={() => handleSaveClick(false)}
            loading={savingAs === "draft"}
            disabled={saving}
          />
          <Button
            label="Save & Publish"
            color="green"
            onClick={() => handleSaveClick(true)}
            loading={savingAs === "publish"}
            disabled={saving}
          />
        </Box>
      </Box>

      <Box className={classes.editorContainer}>
        <EmailEditor
          ref={editorRef}
          onReady={handleEditorReady}
          projectId={unlayerConfig.projectId ?? undefined}
          options={editorOptions}
          minHeight="100%"
          style={{ flex: 1 }}
        />
      </Box>

      <ConfirmDialog
        open={confirmExitOpen}
        question={<>Your unsaved changes will be lost. Discard?</>}
        cancelButtonLabel="Keep editing"
        confirmButtonLabel="Discard"
        onCancel={() => setConfirmExitOpen(false)}
        onConfirm={handleConfirmExit}
      />
    </Box>
  );
};

export default EmailTemplateEditor;
